export type LlmProvider = "requesty" | "bedrock";
export type ModelTier = "thinking" | "small";

export interface TierConfig {
  modelId: string;
  displayName: string;
}

export const PROVIDER_TIER_DEFAULTS: Record<LlmProvider, Record<ModelTier, TierConfig>> = {
  requesty: {
    thinking: { modelId: "anthropic/claude-opus-5-5", displayName: "Claude Opus 5.5" },
    small: { modelId: "fireworks/deepseek-v4-flash", displayName: "DeepSeek V4 Flash" },
  },
  bedrock: {
    thinking: { modelId: "anthropic.claude-opus-4-8", displayName: "Claude Opus 4.8" },
    small: { modelId: "anthropic.claude-sonnet-4-6", displayName: "Claude Sonnet 4.6" },
  },
};

export const ALL_PROVIDERS: LlmProvider[] = ["requesty", "bedrock"];
export const ALL_TIERS: ModelTier[] = ["thinking", "small"];

// Env vars that override the concrete model ID for each provider/tier pair.
// When unset (or blank) the defaults in PROVIDER_TIER_DEFAULTS are used.
export const PROVIDER_TIER_ENV_KEYS: Record<LlmProvider, Record<ModelTier, string>> = {
  requesty: {
    thinking: "REQUESTY_THINKING_MODEL",
    small: "REQUESTY_SMALL_MODEL",
  },
  bedrock: {
    thinking: "BEDROCK_THINKING_MODEL",
    small: "BEDROCK_SMALL_MODEL",
  },
};

// Server-side env override lookup. Guarded so the module stays safe if it is
// ever imported into a browser bundle (where `process` is undefined).
function envModelOverride(provider: LlmProvider, tier: ModelTier): string | undefined {
  if (typeof process === "undefined" || !process.env) return undefined;
  const raw = process.env[PROVIDER_TIER_ENV_KEYS[provider][tier]];
  return raw && raw.trim() ? raw.trim() : undefined;
}

export const PROVIDER_DISPLAY_NAMES: Record<LlmProvider, string> = {
  requesty: "Requesty AI",
  bedrock: "AWS Bedrock",
};

export const TIER_DISPLAY_NAMES: Record<ModelTier, string> = {
  thinking: "Thinking",
  small: "Small",
};

export const TIER_DESCRIPTIONS: Record<ModelTier, string> = {
  thinking: "Deeper reasoning, slower",
  small: "Faster, lighter",
};

export function isValidTier(value: string): value is ModelTier {
  return value === "thinking" || value === "small";
}

export function isValidProvider(value: string): value is LlmProvider {
  return value === "requesty" || value === "bedrock";
}

export function modelIdToTier(modelId: string | null | undefined): ModelTier {
  if (!modelId) return "thinking";
  if (/claude-opus/i.test(modelId)) return "thinking";
  return "small";
}

export function normalizeTier(value: string | null | undefined): ModelTier {
  if (!value) return "thinking";
  if (isValidTier(value)) return value;
  return modelIdToTier(value);
}

export function tierToModelId(provider: LlmProvider, tier: ModelTier): string {
  if (provider === "requesty" && tier === "small") {
    return resolveRequestySmallModel(envModelOverride(provider, tier));
  }
  return envModelOverride(provider, tier) ?? PROVIDER_TIER_DEFAULTS[provider][tier].modelId;
}

// Retire only the unavailable Pro selection; unrelated custom overrides survive.
// Also used by Convex before its first configuration sync.
export function resolveRequestySmallModel(override?: string | null): string {
  const model = override?.trim();
  return !model || model === "fireworks/deepseek-v4-pro"
    ? PROVIDER_TIER_DEFAULTS.requesty.small.modelId
    : model;
}

export function getTierDisplayName(provider: LlmProvider, tier: ModelTier): string {
  return PROVIDER_TIER_DEFAULTS[provider][tier].displayName;
}
